import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts } from '../hooks/use-products';
import { useDeleteProduct } from '../hooks/use-delete-product';
import { useUpdateProductStatus } from '../hooks/use-update-product';
import { ProductCard } from '@/components/organisms/cards';
import { SearchBar } from '@/components/molecules/search-bar';
import { EmptyState } from '@/components/organisms/empty-state';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Switch } from '@/components/atoms/switch';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { StatusBadge } from '@/components/molecules/status-badge';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { formatPrice, getImageUrl, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCategoryTranslation } from '@/hooks/use-category-translation';
import { useCanCreateProduct, useSubscriptionLimits, useSubscriptionStore } from '@/lib/subscription';
import { LimitIndicator } from '@/lib/subscription';
import { toast } from 'sonner';
import type { Product } from '@/lib/types';

export function ProductListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();
  const { translateCategory } = useCategoryTranslation();
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { data, isLoading, error } = useProducts({ search, page, limit: pageSize });
  const deleteProduct = useDeleteProduct();
  const updateProductStatus = useUpdateProductStatus();

  // Subscription gating
  const productAccess = useCanCreateProduct();
  const limits = useSubscriptionLimits();
  const setCurrentCounts = useSubscriptionStore((state) => state.setCurrentCounts);
  const currentShopsCount = useSubscriptionStore((state) => state.currentShopsCount);

  const products = data?.products ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.total;

  // Update products count in subscription store
  React.useEffect(() => {
    if (totalItems !== undefined) {
      setCurrentCounts(currentShopsCount, totalItems);
    }
  }, [totalItems, currentShopsCount, setCurrentCounts]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const handleRowClick = (product: Product) => {
    navigate(`/products/${product.id}`);
  };

  const handleDelete = (product: Product) => {
    deleteProduct.mutate(
      { id: product.refId || product.id },
      {
        onSuccess: () => {
          toast.success(t('products.deleteSuccess', 'Produit supprimé avec succès'));
        },
        onError: () => {
          toast.error(t('products.deleteError', 'Erreur lors de la suppression du produit'));
        },
      }
    );
  };

  const handleToggleStatus = (product: Product) => {
    const newStatus = product.status === 'active' ? 'disabled' : 'active';
    updateProductStatus.mutate(
      { productId: product.refId || product.id, status: newStatus },
      {
        onSuccess: () => {
          toast.success(
            newStatus === 'active'
              ? t('products.activateSuccess', 'Produit activé avec succès')
              : t('products.deactivateSuccess', 'Produit désactivé avec succès')
          );
        },
        onError: () => {
          toast.error(t('products.statusUpdateError', 'Erreur lors de la mise à jour du statut'));
        },
      }
    );
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Product>[] = React.useMemo(
    () => [
      {
        id: 'image',
        header: '',
        cell: (product) => {
          const mainImage = getImageUrl(product.images?.[0]);
          return (
            <div className="w-12 h-12 rounded-md overflow-hidden bg-muted">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                  -
                </div>
              )}
            </div>
          );
        },
        className: 'w-16',
      },
      {
        id: 'name',
        header: t('products.productName'),
        cell: (product) => (
          <div>
            <p className="font-medium line-clamp-1">{product.name}</p>
            {product.categoryName && (
              <p className="text-sm text-muted-foreground">{translateCategory(product.categoryName)}</p>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'price',
        header: t('products.price'),
        cell: (product) => (
          <div>
            <p className="font-bold">
              {formatPrice(getAmountFromPriceObject(product.prices, currency, product.price), currency)}
            </p>
            {product.comparePrice && product.comparePrice > product.price && (
              <p className="text-sm text-muted-foreground line-through">
                {formatPrice(product.comparePrice, currency)}
              </p>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'stock',
        header: t('products.stock'),
        cell: (product) => (
          <Badge variant={product.stock > 0 ? 'secondary' : 'destructive'}>
            {product.stock} {t('products.inStock')}
          </Badge>
        ),
        sortable: true,
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (product) => (
          <div className="flex items-center gap-2">
            <Switch
              checked={product.status === 'active'}
              onCheckedChange={() => handleToggleStatus(product)}
              onClick={(e) => e.stopPropagation()}
              disabled={updateProductStatus.isPending}
            />
            <StatusBadge status={product.status} />
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (product) => (
          <DeleteConfirmDialog
            itemName={product.name}
            onConfirm={() => handleDelete(product)}
            isLoading={deleteProduct.isPending}
          />
        ),
        className: 'w-12',
      },
    ],
    [t, currency, translateCategory, deleteProduct.isPending, handleDelete, updateProductStatus.isPending, handleToggleStatus]
  );

  // Card renderer for mobile view
  const renderCard = (product: Product, index: number) => (
    <ProductCard key={product.id || `product-${index}`} product={product} />
  );

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card className="overflow-hidden">
      <Skeleton className="h-48 w-full" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-6 w-1/3" />
      </div>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('products.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subscription Limit Indicator */}
      <LimitIndicator
        current={limits.products.current}
        max={limits.products.limit}
        label={t('subscription.features.products')}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          onSearch={setSearch}
          placeholder={t('products.searchPlaceholder')}
          className="sm:max-w-xs"
        />
        <Link to="/products/new">
          <Button disabled={!productAccess.canCreate}>
            <Plus className="size-4 mr-2" />
            {t('products.addProduct')}
          </Button>
        </Link>
      </div>

      <ResponsiveDataList
        data={products}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(product, index) => product.id || `product-${index}`}
        cardGridClassName="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyState={
          <EmptyState
            title={t('products.emptyTitle')}
            description={t('products.emptyDescription')}
            action={
              <Link to="/products/new">
                <Button>
                  <Plus className="size-4 mr-2" />
                  {t('products.addProduct')}
                </Button>
              </Link>
            }
          />
        }
      />
    </div>
  );
}
